import { Injectable } from '@angular/core';
import { OrganizationModel } from '../../../../../../common/dist/angular/models/organization.model';
import { OrganizationApiController } from '../api-controllers/organization.api-controller';
import {
  OrganizationPermissionStatusEnum,
} from '../../../../../../common/src/enums/organization-permission-status.enum';
import { OrganizationPermissionEnum } from '../../../../../../common/src/enums/organization-permission.enum';
import { LoggingService } from '../../../../modules/src/lib/core/services/logging.service';
import { AlertMessageFactory } from '../../../../modules/src/lib/core/factories/alert-message.factory';
import { SeverityTypeEnum } from '../../../../modules/src/lib/core/enums/severity-type.enum';
import { AlertMessageStore } from '../../../../modules/src/lib/core/stores/alert-message.store';
import { OrganizationCreationOptions } from '../../../../../../common/src/options/organization-creation.options';
import { CacheStorageService } from '../../../../modules/src/lib/core/services/cache-storage.service';
import { AuthService, LocalStorageCache } from '@auth0/auth0-angular';
import { DataMapper } from '@pristine-ts/data-mapping-common';
import { take } from 'rxjs/operators';
import { MagienoObservabilityManager } from '../../../../modules/src/lib/core/managers/magieno-observability.manager';


@Injectable()
export class OrganizationManager {
  public organization?: OrganizationModel;

  public constructor(private readonly organizationApiController: OrganizationApiController,
                     private readonly loggingService: LoggingService,
                     private readonly cacheStorageService: CacheStorageService,
                     private readonly authService: AuthService,
                     private readonly dataMapper: DataMapper,
                     private readonly observabilityManager: MagienoObservabilityManager,
                     private readonly alertMessageStore: AlertMessageStore) {

  }

  getCachedOrganizationForUser(): Promise<OrganizationModel | null> {
    return new Promise<OrganizationModel | null>((resolve, reject) => {
      this.authService.user$.pipe(take(1)).subscribe(async (user) => {
        try {
          const key = `organization_${user.sub}`;
          const organization = await this.cacheStorageService.get(key);

          if (organization === null) {
            return resolve(null);
          }

          return resolve(await this.dataMapper.autoMap(organization, OrganizationModel));
        } catch (e) {
          reject(e);
        }
      });
    });
  }

  async cacheOrganizationForUser() {
    return new Promise<void>(async (resolve, reject) => {
      this.authService.user$.pipe(take(1)).subscribe(async (user) => {
        try {
          const key = `organization_${user.sub}`;
          await this.cacheStorageService.set(key, this.organization);
          return resolve();
        } catch (e) {
          reject(e);
        }
      });
    });
  }

  async init() {
    if (this.organization === undefined) {
      try {
        const cachedOrganization = await this.getCachedOrganizationForUser();

        if (cachedOrganization !== null) {
          this.organization = cachedOrganization;
          this.loggingService.debug('Organization retrieved from cache.', this.organization);
          return;
        }

        this.organization = await this.organizationApiController.getCurrent();

        // Cache the Organization
        this.cacheOrganizationForUser();

        this.loggingService.debug('Organization retrieved from backend.', this.organization);
      } catch (error) {
        this.loggingService.error('Error retrieving the current Organization.', error);
      }
    }
    return this.organization;
  }

  async getCurrent(): Promise<OrganizationModel | null> {
    if (this.organization === undefined) {
      await this.init();
    }

    return this.organization ?? null;
  }

  public async getPermission(permission: OrganizationPermissionEnum): Promise<OrganizationPermissionStatusEnum> {
    if (this.organization == null) {
      await this.init();
    }

    if (!this.organization?.permissions) {
      return OrganizationPermissionStatusEnum.Deny;
    }

    return this.organization.permissions[permission];

  }

  public async hasPermission(permission: OrganizationPermissionEnum): Promise<boolean> {
    const permissionStatus = await this.getPermission(permission);

    return !(permissionStatus === undefined || permissionStatus === OrganizationPermissionStatusEnum.Deny);
  }

  public async create(organizationCreationOptions: OrganizationCreationOptions): Promise<OrganizationModel> {
    try {
      this.organization = await this.organizationApiController.create(organizationCreationOptions);

      // Delete the token from Auth0, else the user will be stuck in the organization creation process
      const localStorageCache = new LocalStorageCache();
      localStorageCache.allKeys().forEach(key => {
        localStorageCache.remove(key);
      });

      return this.organization;
    } catch (error) {
      try {
        this.authService.user$.pipe(take(1)).subscribe(async (user) => {
          await this.observabilityManager.sendAlert('CRITICAL', 'There was an error creating the Organization', {
            error,
            organizationCreationOptions,
            user,
          });
        });
      } catch (e) {
        await this.observabilityManager.sendAlert('CRITICAL', 'There was an error creating the Organization and couldn\'t retrieve the user.', {
          error,
          organizationCreationOptions,
          error2: e,
        });
      }

      this.loggingService.error('[organization-store] - There was an error creating the Organization', { error });

      this.alertMessageStore.saveAll(AlertMessageFactory.create(error, SeverityTypeEnum.Error));

      throw error;
    }
  }

  public async refresh(): Promise<OrganizationModel | null> {
    try {
      this.organization = await this.organizationApiController.getCurrent();

      // Cache the Organization
      this.cacheOrganizationForUser();
    } catch (error) {
      this.loggingService.error('Error retrieving the current Organization.', error);
      return null;
    }

    return this.organization;
  }
}
